/**
 * Timezone utilities for handling session datetime display and conversion
 */

/**
 * Detect the user's local timezone using the Intl API
 * Falls back to UTC offset if Intl.DateTimeFormat is not available
 * @returns IANA timezone identifier (e.g., "America/New_York", "Europe/Moscow") or UTC offset string
 */
export const getAutoDetectedTimezone = (): string => {
  // Modern browsers - use Intl API for precise timezone detection
  if (typeof Intl !== 'undefined' && Intl.DateTimeFormat) {
    try {
      return Intl.DateTimeFormat().resolvedOptions().timeZone;
    } catch (e) {
      console.warn('Failed to get timezone from Intl API:', e);
    }
  }
  
  // Fallback for older browsers - get UTC offset
  const offset = new Date().getTimezoneOffset();
  const offsetHours = Math.abs(offset / 60);
  const offsetSign = offset > 0 ? '-' : '+';
  return `UTC${offsetSign}${offsetHours}`;
};

/**
 * Get user's timezone preference - either saved preference or auto-detected
 * @returns IANA timezone identifier or UTC offset string
 */
export const getUserTimezone = (): string => {
  // Check for saved preference first
  const savedTimezone = localStorage.getItem('user_timezone');
  if (savedTimezone) {
    return savedTimezone;
  }
  
  // Fall back to auto-detection
  return getAutoDetectedTimezone();
};

/**
 * Save user's timezone preference
 * @param timezone - IANA timezone identifier to save
 */
export const setUserTimezone = (timezone: string): void => {
  localStorage.setItem('user_timezone', timezone);
};

/**
 * Get standard UTC offset for a timezone in +/-H:MM or +/-H format
 * Uses a fixed winter date to avoid DST variations
 * @param timezone - IANA timezone identifier or UTC offset string
 * @returns UTC offset string (e.g., "+3", "-5", "+5:30", "+0")
 */
const getTimezoneOffset = (timezone: string): string => {
  // Handle UTC as special case
  if (timezone === 'UTC') {
    return "+0";
  }
  
  // Handle auto-detected fallback that might be a UTC offset string
  if (timezone.startsWith('UTC')) {
    // Parse various UTC offset formats with mutually exclusive patterns
    // UTC+5, UTC+05, UTC-5 (integer hours)
    const integerMatch = timezone.match(/^UTC([+-])(\d{1,2})$/);
    if (integerMatch) {
      const sign = integerMatch[1];
      const hours = parseInt(integerMatch[2], 10);
      return hours === 0 ? "+0" : `${sign}${hours}`;
    }
    
    // UTC+5:30, UTC+05:30, UTC+0530 (hours with minutes)
    const hmMatch = timezone.match(/^UTC([+-])(\d{1,2}):?(\d{2})$/);
    if (hmMatch) {
      const sign = hmMatch[1];
      const hours = parseInt(hmMatch[2], 10);
      const minutes = Math.min(parseInt(hmMatch[3], 10), 59); // Clamp to valid minutes
      return `${sign}${hours}:${minutes.toString().padStart(2, '0')}`;
    }
    
    // UTC+5.5, UTC+5.75, UTC+5.25 (decimal hours)
    const decimalMatch = timezone.match(/^UTC([+-])(\d{1,2})\.(\d+)$/);
    if (decimalMatch) {
      const sign = decimalMatch[1];
      const hours = parseInt(decimalMatch[2], 10);
      const decimal = parseFloat('0.' + decimalMatch[3]);
      const minutes = Math.round(decimal * 60);
      
      if (minutes === 0) {
        return hours === 0 ? "+0" : `${sign}${hours}`;
      } else {
        return `${sign}${hours}:${minutes.toString().padStart(2, '0')}`;
      }
    }
    
    return "+0"; // Fallback for unparseable UTC strings
  }
  
  try {
    // Use both winter and summer dates to get standard (non-DST) offset
    const winterDate = new Date('2025-01-15T12:00:00Z');
    const summerDate = new Date('2025-07-15T12:00:00Z');
    
    // Calculate offsets for both dates
    const calculateOffset = (referenceDate: Date) => {
      const formatter = new Intl.DateTimeFormat('en-CA', {
        timeZone: timezone,
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit',
        hour12: false
      });
      
      const parts = formatter.formatToParts(referenceDate);
      const partsObj: any = {};
      parts.forEach(part => {
        partsObj[part.type] = part.value;
      });
      
      const utcTimestamp = Date.UTC(
        parseInt(partsObj.year, 10),
        parseInt(partsObj.month, 10) - 1,
        parseInt(partsObj.day, 10),
        parseInt(partsObj.hour, 10),
        parseInt(partsObj.minute, 10),
        parseInt(partsObj.second, 10)
      );
      
      return Math.round((utcTimestamp - referenceDate.getTime()) / (1000 * 60));
    };
    
    const winterOffset = calculateOffset(winterDate);
    const summerOffset = calculateOffset(summerDate);
    
    // Use the minimum numeric offset (standard time, non-DST)
    const standardOffsetMinutes = Math.min(winterOffset, summerOffset);
    
    const offsetHours = Math.floor(Math.abs(standardOffsetMinutes) / 60);
    const offsetMins = Math.abs(standardOffsetMinutes) % 60;
    const sign = standardOffsetMinutes >= 0 ? '+' : '-';
    
    if (offsetMins === 0) {
      return offsetHours === 0 ? "+0" : `${sign}${offsetHours}`;
    } else {
      return `${sign}${offsetHours}:${offsetMins.toString().padStart(2, '0')}`;
    }
  } catch (e) {
    console.warn('Failed to calculate timezone offset for', timezone, e);
    return "+0";
  }
};

/**
 * Get a list of commonly used timezones with their display names in UTC+N City format
 * Always includes the auto-detected timezone at the top of the list
 * @param translate - Translation function for localized names
 * @returns Array of timezone objects with id and display name
 */
export const getTimezoneOptions = (translate?: (key: string) => string) => {
  const autoDetected = getAutoDetectedTimezone();
  
  // Comprehensive timezone list ordered from UTC-12 to UTC+14
  // One city per UTC offset to avoid duplications
  const timezones = [
    { id: 'Etc/GMT+12', key: 'timezone_gmt_minus_12' }, // UTC-12
    { id: 'Pacific/Pago_Pago', key: 'timezone_pago_pago' }, // UTC-11
    { id: 'Pacific/Honolulu', key: 'timezone_honolulu' }, // UTC-10
    { id: 'America/Anchorage', key: 'timezone_anchorage' }, // UTC-9
    { id: 'America/Los_Angeles', key: 'timezone_los_angeles' }, // UTC-8
    { id: 'America/Denver', key: 'timezone_denver' }, // UTC-7
    { id: 'America/Chicago', key: 'timezone_chicago' }, // UTC-6
    { id: 'America/New_York', key: 'timezone_new_york' }, // UTC-5
    { id: 'America/Caracas', key: 'timezone_caracas' }, // UTC-4
    { id: 'America/Sao_Paulo', key: 'timezone_sao_paulo' }, // UTC-3
    { id: 'America/Noronha', key: 'timezone_noronha' }, // UTC-2
    { id: 'Atlantic/Azores', key: 'timezone_azores' }, // UTC-1
    { id: 'Europe/London', key: 'timezone_london' }, // UTC+0
    { id: 'Europe/Paris', key: 'timezone_paris' }, // UTC+1
    { id: 'Europe/Kiev', key: 'timezone_kiev' }, // UTC+2 (Kyiv)
    { id: 'Europe/Moscow', key: 'timezone_moscow' }, // UTC+3
    { id: 'Asia/Dubai', key: 'timezone_dubai' }, // UTC+4
    { id: 'Asia/Karachi', key: 'timezone_karachi' }, // UTC+5
    { id: 'Asia/Dhaka', key: 'timezone_dhaka' }, // UTC+6
    { id: 'Asia/Bangkok', key: 'timezone_bangkok' }, // UTC+7
    { id: 'Asia/Shanghai', key: 'timezone_shanghai' }, // UTC+8
    { id: 'Asia/Tokyo', key: 'timezone_tokyo' }, // UTC+9
    { id: 'Australia/Sydney', key: 'timezone_sydney' }, // UTC+10
    { id: 'Pacific/Noumea', key: 'timezone_noumea' }, // UTC+11
    { id: 'Pacific/Auckland', key: 'timezone_auckland' }, // UTC+12
    { id: 'Pacific/Tongatapu', key: 'timezone_tongatapu' }, // UTC+13
    { id: 'Pacific/Kiritimati', key: 'timezone_kiritimati' }, // UTC+14
  ];

  const mappedTimezones = timezones.map(tz => {
    const offset = getTimezoneOffset(tz.id);
    const cityName = translate ? translate(tz.key) : tz.id.split('/').pop() || tz.id;
    return {
      id: tz.id,
      name: `UTC${offset} ${cityName}`,
      key: tz.key
    };
  });

  // Check if auto-detected timezone is already in the list
  const autoDetectedExists = mappedTimezones.some(tz => tz.id === autoDetected);
  
  if (!autoDetectedExists) {
    // Add auto-detected timezone at the top with special label
    const offset = getTimezoneOffset(autoDetected);
    const autoLabel = translate ? translate('sideMenuSettings_timezoneAuto') : '(automatically)';
    
    let cityName: string;
    if (autoDetected.startsWith('UTC')) {
      // Handle fallback UTC offset string
      cityName = translate ? translate('timezone_utc') : 'UTC';
    } else {
      // Extract city name from IANA timezone
      cityName = autoDetected.split('/').pop() || autoDetected;
    }
    
    mappedTimezones.unshift({
      id: autoDetected,
      name: `UTC${offset} ${cityName} ${autoLabel}`,
      key: 'timezone_auto_detected'
    });
  } else {
    // Mark existing timezone as auto-detected
    mappedTimezones.forEach(tz => {
      if (tz.id === autoDetected) {
        const autoLabel = translate ? translate('sideMenuSettings_timezoneAuto') : '(automatically)';
        tz.name += ` ${autoLabel}`;
      }
    });
  }

  return mappedTimezones;
};

/**
 * Format a timezone-aware datetime string for display in user's local timezone
 * @param datetimeString - Timezone-aware datetime string from DB (e.g., "August 22, 2025 at 8:28:32 PM UTC+3")
 * @param userTimezone - User's timezone (defaults to auto-detected)
 * @param options - Intl.DateTimeFormat options
 * @returns Formatted datetime string in user's timezone
 */
export const formatDatetimeForDisplay = (
  datetimeString: string,
  userTimezone?: string,
  options: Intl.DateTimeFormatOptions = { 
    hour: '2-digit', 
    minute: '2-digit',
    year: 'numeric',
    month: 'long',
    day: 'numeric'
  }
): string => {
  if (!datetimeString) return '';
  
  try {
    const date = new Date(datetimeString);
    if (isNaN(date.getTime())) {
      console.warn('Invalid datetime string:', datetimeString);
      return datetimeString; // Return original if parsing fails
    }
    
    const timezone = userTimezone || getUserTimezone();
    
    // Guard against UTC offset strings that Intl can't handle
    const safeTimezone = timezone.startsWith('UTC') ? 'UTC' : timezone;
    
    return new Intl.DateTimeFormat('en-US', {
      ...options,
      timeZone: safeTimezone
    }).format(date);
  } catch (e) {
    console.warn('Failed to format datetime:', e);
    return datetimeString; // Return original if formatting fails
  }
};

/**
 * Format time only from timezone-aware datetime string with localization support
 * @param datetimeString - Timezone-aware datetime string from DB
 * @param userTimezone - User's timezone (defaults to auto-detected)
 * @param language - Language code for localization ('en', 'ru', 'ua')
 * @returns Time in localized format (12-hour for English, 24-hour for Russian/Ukrainian)
 */
export const formatTimeOnly = (
  datetimeString: string,
  userTimezone?: string,
  language?: string
): string => {
  if (!datetimeString) return '';
  
  try {
    const date = parseTimezoneAwareDatetime(datetimeString);
    if (isNaN(date.getTime())) {
      console.warn('Invalid datetime string:', datetimeString);
      return '';
    }
    
    const timezone = userTimezone || getUserTimezone();
    
    // Guard against UTC offset strings that Intl can't handle
    const safeTimezone = timezone.startsWith('UTC') ? 'UTC' : timezone;
    
    // Determine time format based on language
    const use24Hour = language === 'ru' || language === 'ua';
    
    return new Intl.DateTimeFormat(language === 'en' ? 'en-US' : 'en-GB', {
      hour: '2-digit',
      minute: '2-digit',
      hour12: !use24Hour, // 12-hour for English, 24-hour for Russian/Ukrainian
      timeZone: safeTimezone
    }).format(date);
  } catch (e) {
    console.warn('Failed to format time:', e);
    return '';
  }
};

/**
 * Format only the time portion for session lists using 24-hour format to prevent wrapping
 * @param datetimeString - The datetime string to format
 * @param userTimezone - Optional timezone override
 * @param language - Language for formatting (not used, always 24-hour)
 * @returns Time in compact 24-hour format
 */
export const formatTimeOnlyCompact = (
  datetimeString: string,
  userTimezone?: string,
  language?: string
): string => {
  if (!datetimeString) return '';
  
  try {
    const date = parseTimezoneAwareDatetime(datetimeString);
    if (isNaN(date.getTime())) {
      console.warn('Invalid datetime string:', datetimeString);
      return '';
    }
    
    const timezone = userTimezone || getUserTimezone();
    
    // Guard against UTC offset strings that Intl can't handle
    const safeTimezone = timezone.startsWith('UTC') ? 'UTC' : timezone;
    
    // Always use 24-hour format for compact display
    return new Intl.DateTimeFormat('en-GB', {
      hour: '2-digit',
      minute: '2-digit',
      hour12: false, // Always 24-hour format
      timeZone: safeTimezone
    }).format(date);
  } catch (e) {
    console.warn('Failed to format time:', e);
    return '';
  }
};

/**
 * Format date only (e.g., "Friday, 27 July") from timezone-aware datetime string
 * @param datetimeString - Timezone-aware datetime string from DB
 * @param userTimezone - User's timezone (defaults to auto-detected)
 * @param translate - Translation function for localized day/month names
 * @returns Formatted date string
 */
export const formatDateOnly = (
  datetimeString: string,
  userTimezone?: string,
  translate?: (key: string) => string
): string => {
  if (!datetimeString) return '';
  
  try {
    const date = new Date(datetimeString);
    if (isNaN(date.getTime())) {
      return datetimeString;
    }
    
    const timezone = userTimezone || getUserTimezone();
    
    // Guard against UTC offset strings that Intl can't handle
    const safeTimezone = timezone.startsWith('UTC') ? 'UTC' : timezone;
    
    if (translate) {
      // Use localized day and month names
      const dayKeys = ['day_sunday', 'day_monday', 'day_tuesday', 'day_wednesday', 'day_thursday', 'day_friday', 'day_saturday'];
      const monthKeys = ['month_january', 'month_february', 'month_march', 'month_april', 'month_may', 'month_june', 
                        'month_july', 'month_august', 'month_september', 'month_october', 'month_november', 'month_december'];
      
      // Get date in user's timezone
      const dateInTimezone = new Date(date.toLocaleString('en-US', { timeZone: safeTimezone }));
      const dayIndex = dateInTimezone.getDay();
      const monthIndex = dateInTimezone.getMonth();
      const dayNumber = dateInTimezone.getDate();
      
      const dayName = translate(dayKeys[dayIndex]);
      const monthName = translate(monthKeys[monthIndex]);
      
      return `${dayName}, ${dayNumber} ${monthName}`;
    } else {
      // Use English names
      return new Intl.DateTimeFormat('en-US', {
        timeZone: safeTimezone,
        weekday: 'long',
        day: 'numeric',
        month: 'long'
      }).format(date);
    }
  } catch (e) {
    console.warn('Failed to format date:', e);
    return datetimeString;
  }
};

/**
 * Convert a Date object to timezone-aware datetime string for API (ISO 8601 format)
 * @param date - JavaScript Date object
 * @param userTimezone - User's timezone (defaults to auto-detected)
 * @returns ISO 8601 timezone-aware datetime string (e.g., "2025-09-19T12:41:00+03:00")
 */
export const dateToTimezoneAwareDatetime = (
  date: Date,
  userTimezone?: string
): string => {
  if (!date || isNaN(date.getTime())) {
    throw new Error('Invalid date provided');
  }
  
  try {
    // Always use ISO string format for cross-browser compatibility and database storage
    // This ensures consistent parsing across all browsers and locales
    return date.toISOString();
  } catch (e) {
    console.error('Failed to create timezone-aware datetime:', e);
    throw new Error('Unable to format date to timezone-aware datetime');
  }
};

/**
 * Parse a timezone-aware datetime string into a Date object
 * This function safely handles both ISO 8601 strings and legacy human-readable formats
 * @param datetimeString - Timezone-aware datetime string from DB (ISO 8601 or legacy format)
 * @returns Date object for use in application logic
 */
export const parseTimezoneAwareDatetime = (datetimeString: string): Date => {
  if (!datetimeString) {
    throw new Error('No datetime string provided');
  }
  
  try {
    // First try to parse as-is (handles ISO 8601 and most formats)
    const date = new Date(datetimeString);
    
    // Validate the parsed date
    if (isNaN(date.getTime())) {
      throw new Error('Invalid datetime string: ' + datetimeString);
    }
    
    return date;
  } catch (e) {
    console.error('Failed to parse timezone-aware datetime:', e);
    throw new Error('Unable to parse datetime string: ' + datetimeString);
  }
};